import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';

import AuthService from './AuthService';
import withAuth from './WithAuth';
import CartContext from '../contexts/CartContext';
import { renderRarityBadge } from '../utils/rarityBadge';
import constants from '../config';
import { resolveCardImage } from '../utils/resolveImage';
import { loadShareRef } from '../utils/shareRef';
import { partitionCart, calculateTotalCost, optimizeCart } from '../utils/cartOptimizer';

const Auth = new AuthService();

var TRACKING_UPGRADE_FEE = constants.TRACKING_UPGRADE_FEE || 5.50;
var FREE_SHIPPING_THRESHOLD = constants.FREE_SHIPPING_THRESHOLD || 10;
var TRACKING_REQUIRED_THRESHOLD = constants.TRACKING_REQUIRED_THRESHOLD || 50;
var FLAT_SHIPPING_FEE = constants.FLAT_SHIPPING_FEE || 1.00;

class CartPage extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      validating: true,
      optimizing: false,
      showOptimizeModal: false,
      optimizedCart: null,
      optimizeSwaps: [],
      optimizeSavings: 0,
      optimizeLockedCount: 0,
      optimizeError: null,
      alreadyOptimal: false,
      cartOptimized: false,
      preOptimizeCart: null,
      appliedSavings: 0,
    };
    this.handleOptimize = this.handleOptimize.bind(this);
    this.handleApplyOptimization = this.handleApplyOptimization.bind(this);
    this.handleUndoOptimization = this.handleUndoOptimization.bind(this);
  }

  _cartFingerprint(cart) {
    if (!cart || cart.length === 0) return '';
    return cart.map(function(i) { return i.postId; }).sort().join(',');
  }

  componentDidMount() {
    var cart = this.context.cart;
    this._lastFingerprint = this._cartFingerprint(cart);
    // Check if this exact cart was already checked
    var saved = localStorage.getItem('cart_optimal_fp');
    if (saved && saved === this._lastFingerprint) {
      this.setState({ alreadyOptimal: true });
    }
    this.validateCartItems();
  }

  componentDidUpdate() {
    var fp = this._cartFingerprint(this.context.cart);
    if (fp !== this._lastFingerprint) {
      this._lastFingerprint = fp;
      localStorage.removeItem('cart_optimal_fp');
      if (this.state.alreadyOptimal) {
        this.setState({ alreadyOptimal: false });
      }
    }
  }

  validateCartItems() {
    var self = this;
    var cart = this.context.cart;
    if (!cart || cart.length === 0) {
      this.setState({ validating: false });
      return;
    }
    var promises = cart.map(function(item) {
      return fetch(constants.DOMAIN + '/api/v1/guest/post/' + item.postId, {
        mode: 'cors',
        method: 'GET',
      })
        .then(function(r) {
          if (!r.ok) return { postId: item.postId, remove: true, reason: 'not found' };
          return r.json().then(function(data) {
            if (data.sold) return { postId: item.postId, remove: true, reason: 'sold' };
            if (data.user && data.user.shopActive === false) return { postId: item.postId, remove: true, reason: 'shop closed' };
            var avail = data.quantity || 1;
            if (avail <= 0) return { postId: item.postId, remove: true, reason: 'sold' };
            // Cap cart quantity to available and always store availableQuantity
            if ((item.quantity || 1) > avail) {
              return { postId: item.postId, remove: false, capQty: avail, availableQuantity: avail };
            }
            // Refresh image from backend data
            var refreshedImage = resolveCardImage(data.cardImage) || resolveCardImage(data.images && data.images[0]) || '';
            // Backfill cardId/condition/foil for older cart items
            var enrichFields = null;
            if (!item.cardId || item.condition === undefined || item.foil === undefined) {
              enrichFields = {};
              if (!item.cardId && data.cards && data.cards.length > 0) {
                var fc = data.cards[0];
                enrichFields.cardId = (typeof fc === 'object' && fc._id) ? fc._id : fc;
              }
              if (item.condition === undefined || item.condition === null) {
                enrichFields.condition = data.condition || (data.category ? data.category.replace(/^singles\s*/i, '').trim() : null);
              }
              if (item.foil === undefined) {
                enrichFields.foil = data.foil || false;
              }
            }
            return { postId: item.postId, remove: false, availableQuantity: avail, refreshedImage: refreshedImage, enrichFields: enrichFields };
          });
        })
        .catch(function() {
          return { postId: item.postId, remove: true, reason: 'error' };
        });
    });
    Promise.all(promises).then(function(results) {
      var removed = 0;
      var shopClosed = 0;
      var capped = 0;
      results.forEach(function(r) {
        if (r.remove) {
          self.context.removeFromCart(r.postId);
          if (r.reason === 'shop closed') { shopClosed++; } else { removed++; }
        } else if (r.capQty) {
          self.context.updateCartQuantity(r.postId, r.capQty, r.availableQuantity);
          capped++;
        } else if (r.availableQuantity) {
          // Store available quantity even when not capped, so +/- buttons have the right cap
          var currentItem = self.context.cart.find(function(ci) { return ci.postId === r.postId; });
          if (currentItem && currentItem.availableQuantity !== r.availableQuantity) {
            self.context.updateCartQuantity(r.postId, currentItem.quantity || 1, r.availableQuantity);
          }
        }
        // Refresh image if backend has one and cart item doesn't, and backfill enriched fields
        if (!r.remove) {
          var updateFields = {};
          if (r.refreshedImage) {
            var cartItem = self.context.cart.find(function(ci) { return ci.postId === r.postId; });
            if (cartItem && !cartItem.image) updateFields.image = r.refreshedImage;
          }
          if (r.enrichFields) Object.assign(updateFields, r.enrichFields);
          if (Object.keys(updateFields).length > 0) {
            self.context.updateCartItem(r.postId, updateFields);
          }
        }
      });
      if (removed > 0) {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { text: removed + ' item(s) removed (sold or unavailable)', type: 'error' },
        }));
      }
      if (shopClosed > 0) {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { text: shopClosed + ' item(s) removed (seller\'s shop is closed)', type: 'error' },
        }));
      }
      if (capped > 0) {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { text: capped + ' item(s) quantity reduced to available stock', type: 'error' },
        }));
      }
      self.setState({ validating: false });
    });
  }

  handleOptimize() {
    var self = this;
    var cart = this.context.cart;
    this.setState({ optimizing: true, optimizeError: null });

    var partition = partitionCart(cart, loadShareRef);
    var locked = partition.locked;
    var optimizable = partition.optimizable;

    if (optimizable.length === 0) {
      localStorage.setItem('cart_optimal_fp', this._cartFingerprint(cart));
      this.setState({ optimizing: false, alreadyOptimal: true });
      window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Cart is already optimized', type: 'success' } }));
      return;
    }

    var items = optimizable.map(function(item) {
      return { postId: item.postId, cardId: item.cardId, condition: item.condition || 'NM', foil: item.foil || false };
    });

    fetch(constants.DOMAIN + '/api/v1/guest/cart-alternatives', {
      mode: 'cors',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function(r) {
        if (!r.ok) throw new Error('Server error');
        return r.json();
      })
      .then(function(data) {
        if (!data.alternatives) {
          self.setState({ optimizing: false });
          window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Could not fetch alternatives', type: 'error' } }));
          return;
        }

        var result = optimizeCart(locked, optimizable, data.alternatives);
        var currentCost = calculateTotalCost(cart);
        var newCost = calculateTotalCost(result.newCart);
        var savings = currentCost - newCost;

        if (result.swaps.length === 0 || savings <= 0) {
          localStorage.setItem('cart_optimal_fp', self._cartFingerprint(cart));
          self.setState({ optimizing: false, alreadyOptimal: true });
          window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Cart is already optimized', type: 'success' } }));
          return;
        }

        self.setState({
          optimizing: false,
          showOptimizeModal: true,
          optimizedCart: result.newCart,
          optimizeSwaps: result.swaps,
          optimizeSavings: Math.round(savings * 100) / 100,
          optimizeLockedCount: locked.length,
        });
      })
      .catch(function() {
        self.setState({ optimizing: false });
        window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Failed to optimize cart', type: 'error' } }));
      });
  }

  handleApplyOptimization() {
    if (!this.state.optimizedCart) return;
    var preCart = this.context.cart.slice();
    this.context.replaceCart(this.state.optimizedCart);
    var savings = this.state.optimizeSavings;
    this.setState({
      showOptimizeModal: false,
      optimizedCart: null,
      optimizeSwaps: [],
      optimizeSavings: 0,
      cartOptimized: true,
      preOptimizeCart: preCart,
      appliedSavings: savings,
    });
    window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Cart optimized! Saving $' + savings.toFixed(2), type: 'success' } }));
  }

  handleUndoOptimization() {
    if (!this.state.preOptimizeCart) return;
    this.context.replaceCart(this.state.preOptimizeCart);
    this.setState({ cartOptimized: false, preOptimizeCart: null, appliedSavings: 0 });
    window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Cart restored to original', type: 'info' } }));
  }

  render() {
    var cart = this.context.cart;
    var removeFromCart = this.context.removeFromCart;
    var validating = this.state.validating;
    var api = constants.DOMAIN + '/';

    // Group by seller
    var grouped = {};
    cart.forEach(function(item) {
      var key = item.sellerId || 'unknown';
      if (!grouped[key]) {
        grouped[key] = { sellerName: item.sellerName || 'Seller', items: [] };
      }
      grouped[key].items.push(item);
    });
    var sellerKeys = Object.keys(grouped);

    // Calculate per-seller subtotals first
    var sellerSubtotals = {};
    cart.forEach(function(item) {
      var key = item.sellerId || 'unknown';
      if (!sellerSubtotals[key]) sellerSubtotals[key] = 0;
      sellerSubtotals[key] += (Number(item.price) || 0) * (item.quantity || 1);
    });
    // Platform shipping per seller
    var sellerShipping = {};
    Object.keys(sellerSubtotals).forEach(function(key) {
      sellerShipping[key] = sellerSubtotals[key] >= FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING_FEE;
    });

    var subtotal = 0;
    var shippingTotal = 0;
    var trackingUpgradeTotal = 0;
    var totalItems = 0;
    var trackingSellers = {};
    var shippingCounted = {};
    cart.forEach(function(item) {
      var qty = item.quantity || 1;
      var key = item.sellerId || 'unknown';
      subtotal += (Number(item.price) || 0) * qty;
      // Add flat shipping once per seller
      if (!shippingCounted[key]) {
        shippingCounted[key] = true;
        shippingTotal += sellerShipping[key] || 0;
      }
      // Count tracking fee once per seller group where subtotal < tracking threshold
      if (item.trackingUpgrade && sellerSubtotals[key] < TRACKING_REQUIRED_THRESHOLD && !trackingSellers[key]) {
        trackingSellers[key] = true;
        trackingUpgradeTotal += TRACKING_UPGRADE_FEE;
      }
      totalItems += qty;
    });
    var TRANSACTION_FEE = constants.TRANSACTION_FEE || 0.50;
    var numSellers = Object.keys(sellerSubtotals).length;
    var totalTransactionFee = TRANSACTION_FEE * numSellers;
    var grandTotal = subtotal + shippingTotal + trackingUpgradeTotal + totalTransactionFee;
    var updateCartQuantity = this.context.updateCartQuantity;
    var updateCartItem = this.context.updateCartItem;

    // Optimizer eligibility: 2+ sellers, at least 1 optimizable item, not already checked
    var canOptimize = false;
    if (!validating && numSellers >= 2 && !this.state.alreadyOptimal) {
      var partition = partitionCart(cart, loadShareRef);
      canOptimize = partition.optimizable.length > 0;
    }
    var optimizing = this.state.optimizing;
    var showOptimizeModal = this.state.showOptimizeModal;
    var optimizeSwaps = this.state.optimizeSwaps;
    var optimizeSavings = this.state.optimizeSavings;
    var optimizeLockedCount = this.state.optimizeLockedCount;
    var handleOptimize = this.handleOptimize;
    var handleApplyOptimization = this.handleApplyOptimization;
    var handleUndoOptimization = this.handleUndoOptimization;
    var cartOptimized = this.state.cartOptimized;
    var appliedSavings = this.state.appliedSavings;
    var closeOptimizeModal = function() { this.setState({ showOptimizeModal: false }); }.bind(this);

    return (
      <div>
        <Helmet>
          <title>Cart - Primal Marketplace</title>
        </Helmet>
        <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-10">
          <h1 className="tw-text-2xl tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-8">
            Shopping Cart
          </h1>

          {validating ? (
            <div className="tw-text-center tw-py-12">
              <div className="tw-text-[#999] dark:tw-text-gray-500 tw-text-sm">Checking cart items...</div>
            </div>
          ) : cart.length === 0 ? (
            <div className="tw-text-center tw-py-20">
              <div className="tw-w-16 tw-h-16 tw-mx-auto tw-mb-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a]">
                <i className="fa fa-shopping-cart tw-text-2xl tw-text-[#ccc] dark:tw-text-[#444]" />
              </div>
              <p className="tw-text-lg tw-font-semibold tw-text-[#555] dark:tw-text-gray-400 tw-mb-1.5">
                Your cart is empty
              </p>
              <p className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-mb-8">
                Browse listings and add items to your cart
              </p>
              <Link
                to="/browse"
                className="tw-inline-block tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-py-3 tw-px-8 tw-rounded-xl tw-text-sm tw-no-underline"
                style={{ boxShadow: '0 0 20px rgba(201, 168, 76, 0.2)', transition: 'transform 250ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 250ms ease, background-color 250ms ease' }}
                onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(201, 168, 76, 0.4)'; }}
                onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 0 20px rgba(201, 168, 76, 0.2)'; }}
              >
                Browse Cards
              </Link>
            </div>
          ) : (
            <React.Fragment>
            {/* Optimizer banner — show optimize or undo */}
            {cartOptimized ? (
              <div className="tw-mb-6 tw-bg-[#f0fdf4] dark:tw-bg-[#052e16] tw-border tw-border-green-200 dark:tw-border-green-900 tw-rounded-xl tw-p-4 tw-flex tw-items-center tw-gap-4">
                <div className="tw-flex-shrink-0 tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}>
                  <i className="fa fa-check tw-text-white tw-text-sm" />
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">Cart optimized</div>
                  <div className="tw-text-xs tw-text-[#666] dark:tw-text-gray-400 tw-mt-0.5">
                    {'You\'re saving '}
                    <span className="tw-text-green-600 dark:tw-text-green-400 tw-font-semibold">${appliedSavings.toFixed(2)}</span>
                    {' with fewer sellers'}
                  </div>
                </div>
                <button
                  onClick={handleUndoOptimization}
                  className="tw-flex-shrink-0 tw-bg-transparent hover:tw-bg-[#f5f5f5] dark:hover:tw-bg-[#252525] tw-text-[#666] dark:tw-text-gray-400 tw-font-semibold tw-py-2 tw-px-4 tw-rounded-lg tw-text-xs tw-border tw-border-[#ddd] dark:tw-border-[#333] tw-cursor-pointer tw-transition-colors"
                >
                  Undo
                </button>
              </div>
            ) : this.state.alreadyOptimal ? (
              <div className="tw-mb-6 tw-bg-[#f0fdf4] dark:tw-bg-[#052e16] tw-border tw-border-green-200 dark:tw-border-green-900 tw-rounded-xl tw-p-4 tw-flex tw-items-center tw-gap-4">
                <div className="tw-flex-shrink-0 tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}>
                  <i className="fa fa-check tw-text-white tw-text-sm" />
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">Cart is already optimized</div>
                  <div className="tw-text-xs tw-text-[#666] dark:tw-text-gray-400 tw-mt-0.5">You already have the best prices available</div>
                </div>
              </div>
            ) : canOptimize ? (
              <div className="tw-mb-6 tw-bg-[#f0f7ff] dark:tw-bg-[#0d1b2a] tw-border tw-border-[#b3d4fc] dark:tw-border-[#1e3a5f] tw-rounded-xl tw-p-4 tw-flex tw-items-center tw-gap-4">
                <div className="tw-flex-shrink-0 tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
                  <i className="fa fa-magic tw-text-white tw-text-sm" />
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">Save on shipping & fees</div>
                  <div className="tw-text-xs tw-text-[#666] dark:tw-text-gray-400 tw-mt-0.5">We can find the same cards from fewer sellers to reduce costs</div>
                </div>
                <button
                  onClick={handleOptimize}
                  disabled={optimizing}
                  className="tw-flex-shrink-0 tw-bg-[#3b82f6] hover:tw-bg-[#2563eb] tw-text-white tw-font-semibold tw-py-2 tw-px-4 tw-rounded-lg tw-text-xs tw-border-none tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                  {optimizing ? 'Optimizing...' : 'Optimize Cart'}
                </button>
              </div>
            ) : null}

            <div className="lg:tw-flex lg:tw-gap-8 lg:tw-items-start">
              {/* Left column — Cart items */}
              <div className="lg:tw-flex-1 tw-min-w-0">
              {sellerKeys.map(function(sellerId) {
                var group = grouped[sellerId];
                return (
                  <div key={sellerId} className="tw-mb-6">
                    <div className="tw-text-xs tw-font-semibold tw-text-[#999] dark:tw-text-gray-500 tw-uppercase tw-tracking-wider tw-mb-3">
                      Sold by {group.sellerName}
                    </div>
                    <div className="tw-bg-white dark:tw-bg-[#141414] tw-rounded-xl tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-overflow-hidden" style={{ transition: 'border-color 250ms ease, box-shadow 250ms ease' }}>
                      {group.items.map(function(item, idx) {
                        var imgSrc = resolveCardImage(item.image) || resolveCardImage(item.cardImage) || null;
                        var isFirstItem = idx === 0;
                        var flatShipping = sellerShipping[sellerId] || 0;
                        return (
                          <div
                            key={item.postId}
                            className={'tw-flex tw-items-center tw-gap-5 tw-p-5' + (idx > 0 ? ' tw-border-t tw-border-[#eee] dark:tw-border-[#2a2a2a]' : '')}
                          >
                            <Link to={'/product/' + item.postId} className="tw-flex-shrink-0">
                              {imgSrc ? (
                                <img
                                  src={imgSrc}
                                  alt={item.title}
                                  className="tw-w-18 tw-h-18 tw-rounded-lg tw-object-contain tw-border tw-border-[#eee] dark:tw-border-[#2a2a2a] tw-bg-white dark:tw-bg-[#1a1a1a]"
                                  style={{ width: '72px', height: '72px' }}
                                />
                              ) : (
                                <div className="tw-rounded-lg tw-border tw-border-[#eee] dark:tw-border-[#2a2a2a] tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-flex tw-items-center tw-justify-center" style={{ width: '72px', height: '72px' }}>
                                  <i className="fa fa-image tw-text-[#ccc] dark:tw-text-[#555]" />
                                </div>
                              )}
                            </Link>
                            <div className="tw-flex-1 tw-min-w-0">
                              <Link
                                to={'/product/' + item.postId}
                                className="tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white hover:tw-text-brand-gold tw-no-underline tw-transition-colors tw-truncate tw-block"
                              >
                                {item.title}
                              </Link>
                              <div className="tw-text-xs tw-mt-0.5">
                                <span className="tw-text-[#1a1a1a] dark:tw-text-white">{'$' + Number(item.price).toFixed(2)}</span>
                                {isFirstItem && flatShipping > 0 ? (
                                  <span className="tw-text-[#999] dark:tw-text-gray-500">{'\u00a0+ $' + flatShipping.toFixed(2) + ' shipping'}</span>
                                ) : (
                                  <span className="tw-text-green-600 dark:tw-text-green-400 tw-inline-flex tw-items-center tw-gap-1 tw-ml-1.5" style={{ verticalAlign: 'middle' }}><i className="fa fa-truck" style={{ fontSize: '9px' }} />Shipping Included</span>
                                )}
                              </div>
                              {item.rarity ? (
                                <div className="tw-mt-1">
                                  {renderRarityBadge(item.rarity, null)}
                                </div>
                              ) : null}
                            </div>
                            {/* Quantity — show +/- controls only if more than 1 available */}
                            {(item.availableQuantity || 1) > 1 ? (
                            <div className="tw-inline-grid tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-rounded-lg tw-overflow-hidden tw-flex-shrink-0" style={{ gridTemplateColumns: '32px 32px 32px' }}>
                              <button
                                type="button"
                                onClick={function() {
                                  var q = (item.quantity || 1) - 1;
                                  if (q < 1) { removeFromCart(item.postId); }
                                  else { updateCartQuantity(item.postId, q); }
                                }}
                                className="tw-h-8 tw-flex tw-items-center tw-justify-center tw-bg-transparent hover:tw-bg-[#f5f5f5] dark:hover:tw-bg-[#222] tw-border-none tw-cursor-pointer tw-text-[#999] hover:tw-text-brand-gold tw-text-sm tw-font-bold tw-transition-colors"
                              >
                                -
                              </button>
                              <span className="tw-h-8 tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-border-l tw-border-r tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a]">
                                {item.quantity || 1}
                              </span>
                              <button
                                type="button"
                                onClick={function() {
                                  var q = (item.quantity || 1) + 1;
                                  var max = item.availableQuantity || (item.quantity || 1);
                                  if (q <= max) updateCartQuantity(item.postId, q);
                                }}
                                className="tw-h-8 tw-flex tw-items-center tw-justify-center tw-bg-transparent hover:tw-bg-[#f5f5f5] dark:hover:tw-bg-[#222] tw-border-none tw-cursor-pointer tw-text-[#999] hover:tw-text-brand-gold tw-text-sm tw-font-bold tw-transition-colors"
                              >
                                +
                              </button>
                            </div>
                            ) : (
                              <span className="tw-text-xs tw-font-medium tw-text-[#999] dark:tw-text-gray-500 tw-flex-shrink-0">Qty: 1</span>
                            )}
                            <div className="tw-text-right tw-flex-shrink-0">
                              <div className="tw-text-sm tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1">
                                {'$' + (Number(item.price) * (item.quantity || 1)).toFixed(2)}
                              </div>
                              <button
                                onClick={function() { removeFromCart(item.postId); }}
                                className="tw-text-xs tw-text-[#bbb] dark:tw-text-[#555] hover:tw-text-red-500 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {sellerSubtotals[sellerId] < TRACKING_REQUIRED_THRESHOLD && (
                      <label className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-3 tw-mt-2 tw-cursor-pointer tw-rounded-lg tw-bg-[#f9f9f9] dark:tw-bg-[#111] tw-border tw-border-[#eee] dark:tw-border-[#2a2a2a] tw-transition-colors hover:tw-border-brand-gold/30">
                        <input
                          type="checkbox"
                          checked={group.items.some(function(i) { return i.trackingUpgrade; })}
                          onChange={function() {
                            var hasTracking = group.items.some(function(i) { return i.trackingUpgrade; });
                            group.items.forEach(function(i) {
                              if (updateCartItem) {
                                updateCartItem(i.postId, { trackingUpgrade: !hasTracking });
                              }
                            });
                          }}
                          className="tw-w-4 tw-h-4 tw-flex-shrink-0 tw-accent-brand-gold"
                        />
                        <div>
                          <span className="tw-text-xs tw-font-medium tw-text-[#666] dark:tw-text-gray-400 tw-leading-normal">
                            Add tracking for this package
                          </span>
                          <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-ml-1.5">
                            +${TRACKING_UPGRADE_FEE.toFixed(2)}
                          </span>
                        </div>
                      </label>
                    )}
                  </div>
                );
              })}
              </div>

              {/* Right column — Sticky order summary */}
              <div className="lg:tw-w-80 lg:tw-flex-shrink-0 lg:tw-sticky lg:tw-top-24">
                <div className="tw-bg-white dark:tw-bg-[#141414] tw-rounded-xl tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-p-6 tw-mb-5">
                  <h2 className="tw-text-base tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-5">Order Summary</h2>
                  <div className="tw-flex tw-justify-between tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-2">
                    <span>Subtotal ({totalItems} item{totalItems !== 1 ? 's' : ''})</span>
                    <span>{'$' + subtotal.toFixed(2)}</span>
                  </div>
                  <div className="tw-flex tw-justify-between tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-2">
                    <span>{shippingTotal === 0 ? 'Shipping + Tracking' : 'Shipping'}</span>
                    {shippingTotal > 0 ? (
                      <span>{'$' + shippingTotal.toFixed(2)}</span>
                    ) : (
                      <span className="tw-text-green-600 dark:tw-text-green-400">Included</span>
                    )}
                  </div>
                  {trackingUpgradeTotal > 0 && (
                    <div className="tw-flex tw-justify-between tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-2">
                      <span>Tracking Upgrades</span>
                      <span>{'$' + trackingUpgradeTotal.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="tw-flex tw-justify-between tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-2">
                    <span>Transaction Fee{numSellers > 1 ? ' ($' + TRANSACTION_FEE.toFixed(2) + ' \u00d7 ' + numSellers + ' sellers)' : ''}</span>
                    <span>{'$' + totalTransactionFee.toFixed(2)}</span>
                  </div>
                  <div className="tw-flex tw-justify-between tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-2">
                    <span>Estimated Tax</span>
                    <span className="tw-italic tw-text-[#999] dark:tw-text-gray-500" style={{ fontSize: '12px' }}>Calculated at checkout</span>
                  </div>
                  <div className="tw-border-t tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-pt-4 tw-mt-1 tw-flex tw-justify-between tw-items-baseline">
                    <span className="tw-text-base tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">Total</span>
                    <span className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">{'$' + grandTotal.toFixed(2)}</span>
                  </div>
                </div>

                <button
                  onClick={() => this.props.history.push('/cart/checkout')}
                  className="tw-w-full tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-py-3.5 tw-rounded-xl tw-text-sm tw-cursor-pointer tw-border-none"
                  style={{ boxShadow: '0 0 20px rgba(201, 168, 76, 0.2)', transition: 'transform 250ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 250ms ease, background-color 250ms ease' }}
                  onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(201, 168, 76, 0.4)'; }}
                  onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 0 20px rgba(201, 168, 76, 0.2)'; }}
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
            </React.Fragment>
          )}
        </div>

        {/* Optimize comparison modal */}
        {showOptimizeModal && (
          <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
            <div className="tw-bg-white dark:tw-bg-[#1c1c1e] tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-md tw-max-h-[85vh] tw-overflow-y-auto tw-border tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a]">
              {/* Header */}
              <div className="tw-p-5 tw-pb-4">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-flex tw-items-center tw-gap-2.5">
                    <div className="tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}>
                      <i className="fa fa-check tw-text-white" style={{ fontSize: '11px' }} />
                    </div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0">Optimize Cart</h3>
                  </div>
                  <button onClick={closeOptimizeModal} className="tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-[#999] hover:tw-text-[#333] dark:hover:tw-text-white tw-text-base tw-p-1 tw-leading-none">
                    <i className="fa fa-times" />
                  </button>
                </div>
              </div>

              {/* Savings comparison */}
              <div className="tw-mx-5 tw-rounded-xl tw-p-4 tw-mb-4" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(22,163,74,0.15))' }}>
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-5">
                  <div className="tw-text-center">
                    <div className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-[#999] dark:tw-text-gray-500 tw-mb-1 tw-font-semibold">Before</div>
                    <div className="tw-text-lg tw-font-bold tw-text-[#999] dark:tw-text-gray-400" style={{ textDecoration: 'line-through', textDecorationColor: 'rgba(239,68,68,0.5)' }}>{'$' + grandTotal.toFixed(2)}</div>
                  </div>
                  <span className="tw-text-green-500 tw-text-lg" style={{ lineHeight: 1, marginTop: '14px' }}>{'\u2192'}</span>
                  <div className="tw-text-center">
                    <div className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-green-600 dark:tw-text-green-400 tw-mb-1 tw-font-semibold">After</div>
                    <div className="tw-text-xl tw-font-bold tw-text-green-600 dark:tw-text-green-400">{'$' + (grandTotal - optimizeSavings).toFixed(2)}</div>
                  </div>
                </div>
                <div className="tw-text-center tw-mt-3 tw-pt-3 tw-border-t tw-border-green-200 dark:tw-border-green-900">
                  <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-green-700 dark:tw-text-green-300 tw-text-sm tw-font-bold">
                    <i className="fa fa-tag" style={{ fontSize: '10px' }} />
                    Save ${optimizeSavings.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Swaps list */}
              <div className="tw-px-5 tw-pb-4">
                <div className="tw-text-[10px] tw-font-bold tw-text-[#999] dark:tw-text-gray-500 tw-uppercase tw-tracking-widest tw-mb-3">
                  {optimizeSwaps.length} Item{optimizeSwaps.length !== 1 ? 's' : ''} Swapped
                </div>
                {optimizeSwaps.map(function(swap, idx) {
                  var priceDiff = Number(swap.from.price) - Number(swap.to.price);
                  return (
                    <div key={idx} className="tw-rounded-lg tw-border tw-border-[#eee] dark:tw-border-[#2a2a2a] tw-p-3.5 tw-mb-2">
                      {/* Card name */}
                      <div className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-3">
                        {swap.from.title}
                      </div>
                      {/* From → To row */}
                      <div className="tw-flex tw-items-stretch tw-gap-2">
                        {/* From seller */}
                        <div className="tw-flex-1 tw-min-w-0 tw-rounded-lg tw-px-3 tw-py-2.5 tw-bg-[#fafafa] dark:tw-bg-[#222]">
                          <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1.5">
                            <span className="tw-text-xs tw-font-medium tw-text-[#666] dark:tw-text-gray-400 tw-truncate">{swap.from.sellerName}</span>
                            {swap.from.sellerRating > 0 && (
                              <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-flex-shrink-0 tw-text-yellow-500" style={{ fontSize: '10px', lineHeight: 1 }}>
                                <i className="fa fa-star" style={{ fontSize: '9px' }} />
                                {swap.from.sellerRating.toFixed(1)}
                              </span>
                            )}
                          </div>
                          <div className="tw-text-sm tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">${Number(swap.from.price).toFixed(2)}</div>
                          {swap.conditionChange && <div className="tw-text-[10px] tw-text-[#999] tw-mt-1">{swap.conditionChange.from}</div>}
                        </div>
                        {/* Arrow */}
                        <div className="tw-flex tw-items-center tw-flex-shrink-0 tw-mx-1">
                          <div className="tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center" style={{ background: 'linear-gradient(135deg, #d4a843, #b8922e)' }}>
                            <i className="fa fa-arrow-right tw-text-white" style={{ fontSize: '10px' }} />
                          </div>
                        </div>
                        {/* To seller */}
                        <div className="tw-flex-1 tw-min-w-0 tw-rounded-lg tw-px-3 tw-py-2.5 tw-border tw-border-green-200 dark:tw-border-green-900" style={{ background: 'rgba(34,197,94,0.06)' }}>
                          <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1.5">
                            <span className="tw-text-xs tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-truncate">{swap.to.sellerName}</span>
                            {swap.to.sellerRating > 0 && (
                              <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-flex-shrink-0 tw-text-yellow-500" style={{ fontSize: '10px', lineHeight: 1 }}>
                                <i className="fa fa-star" style={{ fontSize: '9px' }} />
                                {swap.to.sellerRating.toFixed(1)}
                              </span>
                            )}
                          </div>
                          <div className="tw-text-sm tw-font-bold tw-text-green-600 dark:tw-text-green-400">${Number(swap.to.price).toFixed(2)}</div>
                          {swap.conditionChange && (
                            <div className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-green-600 dark:tw-text-green-400 tw-mt-1 tw-font-medium">
                              {swap.conditionChange.to} <i className="fa fa-arrow-up" style={{ fontSize: '8px' }} />
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Price savings tag */}
                      {priceDiff > 0 && (
                        <div className="tw-mt-2.5 tw-flex tw-justify-end">
                          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-green-600 dark:tw-text-green-400 tw-font-semibold">
                            <i className="fa fa-check-circle" style={{ fontSize: '10px' }} />
                            ${priceDiff.toFixed(2)} cheaper
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {optimizeLockedCount > 0 && (
                  <div className="tw-mt-2 tw-text-[11px] tw-text-[#999] dark:tw-text-gray-500 tw-flex tw-items-center tw-gap-1.5">
                    <i className="fa fa-lock" style={{ fontSize: '9px' }} />
                    {optimizeLockedCount} item{optimizeLockedCount !== 1 ? 's' : ''} locked (referral links)
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="tw-p-5 tw-pt-3 tw-flex tw-gap-3">
                <button
                  onClick={closeOptimizeModal}
                  className="tw-flex-1 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-semibold tw-border tw-border-[#ddd] dark:tw-border-[#333] tw-bg-transparent tw-text-[#666] dark:tw-text-gray-400 tw-cursor-pointer tw-transition-all hover:tw-bg-[#f5f5f5] dark:hover:tw-bg-[#252525]"
                >
                  Keep Current
                </button>
                <button
                  onClick={handleApplyOptimization}
                  className="tw-flex-1 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-bold tw-border-none tw-text-white tw-cursor-pointer tw-transition-all hover:tw-shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
                >
                  Apply Optimization
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withAuth(CartPage);
